/**
 * Suggestion tooltip and quick menu for accepting/discarding suggestions
 */

import {EditorView, hoverTooltip} from "@codemirror/view";
import {Suggestion, Token} from "../models/editor.types";
import {suggestionsField, setSuggestionsEffect, tokensField, setTokensEffect} from "../state/editor.state";

// ===== Suggestion Hover Tooltip =====
export const suggestionHoverTooltip = hoverTooltip((view, pos) => {
  const suggestions = view.state.field(suggestionsField);

  // Find suggestion at cursor position
  const suggestion = suggestions.find((s) => s.from <= pos && pos < s.to);

  if (!suggestion) return null;

  // Create tooltip DOM element
  const dom = document.createElement("div");
  dom.className = "suggestion-tooltip";

  // Style the tooltip
  Object.assign(dom.style, {
    padding: '6px 10px',
    background: '#2b2b2b',
    color: '#ffffff',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    border: '1px solid #3a3a3a',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
  });

  // Add icon and text
  const icon = document.createElement("span");
  icon.textContent = "💡 ";
  icon.style.marginRight = "4px";

  const text = document.createElement("span");
  text.textContent = `Suggested: ${suggestion.suggestedType}`;

  const hint = document.createElement("div");
  hint.style.fontSize = "11px";
  hint.style.opacity = "0.7";
  hint.style.marginTop = "2px";
  hint.textContent = "Click to accept or discard";

  dom.appendChild(icon);
  dom.appendChild(text);
  dom.appendChild(hint);

  return {
    pos: suggestion.from,
    end: suggestion.to,
    above: true,
    create: () => ({dom})
  };
}, {
  // Show tooltip immediately without delay
  hoverTime: 0
});

// ===== Suggestion Quick Menu =====
export class SuggestionQuickMenu {
  private menuElement: HTMLElement | null = null;
  private currentSuggestion: Suggestion | null = null;
  private view: EditorView | null = null;

  show(view: EditorView, suggestion: Suggestion, targetElement: HTMLElement): void {
    this.hide(); // Remove any existing menu first

    // Force remove any CodeMirror tooltips that might be showing
    const existingTooltips = document.querySelectorAll('.cm-tooltip');
    existingTooltips.forEach(tooltip => tooltip.remove());

    // Now set the new view and suggestion
    this.view = view;
    this.currentSuggestion = suggestion;

    const menu = document.createElement("div");
    menu.className = "suggestion-quick-menu";

    // Apply inline styles for dark theme
    Object.assign(menu.style, {
      display: 'flex',
      gap: '4px',
      padding: '4px',
      background: '#2b2b2b',
      border: '1px solid #3a3a3a',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
      zIndex: '100000',
      position: 'fixed'
    });

    const acceptBtn = this.createAcceptButton();
    const discardBtn = this.createDiscardButton();

    menu.appendChild(acceptBtn);
    menu.appendChild(discardBtn);

    // Position the menu below the suggestion
    const rect = targetElement.getBoundingClientRect();
    menu.style.top = `${rect.bottom + 4}px`;
    menu.style.left = `${rect.left}px`;

    document.body.appendChild(menu);
    this.menuElement = menu;

    // Setup event listeners
    setTimeout(() => {
      document.addEventListener("click", this.handleOutsideClick);
      window.addEventListener("scroll", this.hide, true);
      window.addEventListener("resize", this.hide);
    }, 0);
  }

  private createAcceptButton(): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.className = "suggestion-btn suggestion-btn-accept";
    btn.title = "Accept suggestion";
    btn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.accept();
    };

    Object.assign(btn.style, {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      padding: '0',
      border: 'none',
      borderRadius: '6px',
      background: 'transparent',
      cursor: 'pointer',
      transition: 'background 0.2s ease'
    });

    btn.onmouseenter = () => btn.style.background = 'rgba(255, 255, 255, 0.1)';
    btn.onmouseleave = () => btn.style.background = 'transparent';

    const icon = document.createElement("span");
    icon.innerHTML = "✓";
    icon.className = "suggestion-icon";
    Object.assign(icon.style, {
      fontSize: '16px',
      fontWeight: 'bold',
      lineHeight: '1',
      color: '#4caf50'
    });
    btn.appendChild(icon);

    return btn;
  }

  private createDiscardButton(): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.className = "suggestion-btn suggestion-btn-discard";
    btn.title = "Discard suggestion";
    btn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.discard();
    };

    Object.assign(btn.style, {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      padding: '0',
      border: 'none',
      borderRadius: '6px',
      background: 'transparent',
      cursor: 'pointer',
      transition: 'background 0.2s ease'
    });

    btn.onmouseenter = () => btn.style.background = 'rgba(255, 255, 255, 0.1)';
    btn.onmouseleave = () => btn.style.background = 'transparent';

    const icon = document.createElement("span");
    icon.innerHTML = "✕";
    icon.className = "suggestion-icon";
    Object.assign(icon.style, {
      fontSize: '16px',
      fontWeight: 'bold',
      lineHeight: '1',
      color: '#f44336'
    });
    btn.appendChild(icon);

    return btn;
  }

  private handleOutsideClick = (e: MouseEvent): void => {
    if (this.menuElement && !this.menuElement.contains(e.target as Node)) {
      this.hide();
    }
  };

  hide = (): void => {
    if (this.menuElement) {
      this.menuElement.remove();
      this.menuElement = null;
    }
    document.removeEventListener("click", this.handleOutsideClick);
    window.removeEventListener("scroll", this.hide, true);
    window.removeEventListener("resize", this.hide);
    this.currentSuggestion = null;
  };

  private accept = (): void => {
    if (!this.view || !this.currentSuggestion) return;

    const state = this.view.state;
    const suggestions = state.field(suggestionsField);
    const tokens = state.field(tokensField);

    // Remove suggestion by position comparison
    const currentFrom = this.currentSuggestion.from;
    const currentTo = this.currentSuggestion.to;
    const newSuggestions = suggestions.filter(
      (s) => !(s.from === currentFrom && s.to === currentTo)
    );

    // Create new token
    const newToken: Token = {
      from: this.currentSuggestion.from,
      to: this.currentSuggestion.to,
      type: this.currentSuggestion.suggestedType,
    };

    this.view.dispatch({
      effects: [
        setSuggestionsEffect.of(newSuggestions),
        setTokensEffect.of([...tokens, newToken]),
      ],
    });

    setTimeout(() => this.hide(), 10);
  };

  private discard = (): void => {
    if (!this.view || !this.currentSuggestion) return;

    const state = this.view.state;
    const suggestions = state.field(suggestionsField);

    // Remove suggestion by position comparison
    const currentFrom = this.currentSuggestion.from;
    const currentTo = this.currentSuggestion.to;
    const newSuggestions = suggestions.filter(
      (s) => !(s.from === currentFrom && s.to === currentTo)
    );

    this.view.dispatch({
      effects: setSuggestionsEffect.of(newSuggestions),
    });

    setTimeout(() => this.hide(), 10);
  };
}

export const suggestionQuickMenu = new SuggestionQuickMenu();
